import { useQuery, useMutation } from '@apollo/client'
import { Trans } from '@lingui/macro'
import { useGlobals } from 'context'
import filter from 'lodash/filter'
import React, { useContext, useEffect, useState } from 'react'
import { CHANGE_SETTING, CHANGE_SETTINGS } from '../mutations'
import { GET_SETTINGS } from '../queries'

const SettingContext = React.createContext()

function formatSetting(setting) {
  return {
    id: setting.plan_settings_id,
    changed: false,
    value:
      setting.setting_value === 'true'
        ? true
        : setting.setting_value === 'false'
          ? false
          : setting.setting_value,
  }
}

function formatSettings(settings) {
  let formattedSettings = {}
  settings.forEach((setting) => {
    const key = setting.setting_key.split(' ').join('_')
    formattedSettings = {
      ...formattedSettings,
      [key]: formatSetting(setting),
    }
  })
  return formattedSettings
}

function SettingProvider({ children }) {
  const { createError, createSuccess } = useGlobals()
  const [settings, setSettings] = useState([])
  const { loading, data, refetch } = useQuery(GET_SETTINGS)
  const { settings: cachedSettings = [] } = data || {}

  const initialSettings = React.useMemo(() => {
    if (cachedSettings.length) {
      return formatSettings(cachedSettings)
    }
  }, [cachedSettings])

  const settingUpdate = (cache, { data: { changeSetting } }) => {
    try {
      const cached = cache.readQuery({
        query: GET_SETTINGS,
      })
      cache.writeQuery({
        query: GET_SETTINGS,
        data: {
          settings: cached.settings.map((setting) => {
            if (setting.plan_settings_id === changeSetting.plan_settings_id) {
              return changeSetting
            }
            return setting
          }),
        },
      })
    } catch (error) {
      // Event was not in cache
    }
  }
  const settingsUpdate = (cache, { data: { changeSettings } }) => {
    try {
      const cached = cache.readQuery({
        query: GET_SETTINGS,
      })
      cache.writeQuery({
        query: GET_SETTINGS,
        data: {
          settings: cached.settings.map((setting) => {
            const changeSetting =
              changeSettings.find(
                (_setting) =>
                  _setting.plan_settings_id === setting.plan_settings_id,
              ) || null
            if (changeSetting !== null) {
              return changeSetting
            }
            return setting
          }),
        },
      })
    } catch (error) {
      // Event was not in cache
    }
  }
  const [changeSetting] = useMutation(CHANGE_SETTING, {
    update: settingUpdate,
  })
  const [changeSettings] = useMutation(CHANGE_SETTINGS, {
    update: settingsUpdate,
  })

  useEffect(() => {
    if (!loading && cachedSettings.length > 0) {
      setSettings(formatSettings(cachedSettings))
    }
  }, [loading, cachedSettings])

  async function updateSetting(setting) {
    try {
      const input = {
        plan_settings_id: setting.id,
        setting_value: `${setting.value}`,
      }
      await changeSetting({
        variables: {
          input,
        },
      })
      createSuccess(<Trans>Setting</Trans>, 'update')
    } catch (error) {
      createError(error, <Trans>Setting</Trans>, 'update')
    }
  }

  async function updateSettings(settings) {
    try {
      const changedSettings = filter(settings, (setting, key) => {
        return setting.changed === true
      })
      const input = changedSettings.map((setting) => {
        return {
          plan_settings_id: setting.id,
          setting_value: `${setting.value}`,
        }
      })
      if (input.length > 0) {
        await changeSettings({
          variables: {
            input,
          },
        })
        createSuccess(<Trans>Settings</Trans>, 'update')
      }
    } catch (error) {
      createError(error, <Trans>Settings</Trans>, 'update')
    }
  }

  return (
    <SettingContext.Provider
      value={{
        loading,
        refetch,
        initialSettings,
        settings,
        setSettings,
        updateSetting,
        updateSettings,
      }}
    >
      {children}
    </SettingContext.Provider>
  )
}

function useSettings() {
  return useContext(SettingContext)
}

export { SettingProvider, useSettings }
